import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

/**
 * Generic hook for type-safe URL query parameters with Zod validation
 *
 * @template T - Zod schema type
 * @param schema - Zod schema for validation
 * @returns Tuple of [params, setParams]
 *
 * @example
 * ```ts
 * const schema = z.object({
 *   view: z.enum(['list', 'chart']).catch('list'),
 *   sortBy: z.string().catch('type')
 * });
 *
 * const [params, setParams] = useTypedSearchParams(schema);
 * setParams({ view: 'chart' }); // Type-safe!
 * ```
 */
export function useTypedSearchParams<T extends z.ZodObject<any>>(schema: T) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse and validate current params
  const params = useMemo(() => {
    const raw = Object.fromEntries(searchParams.entries());
    const result = schema.safeParse(raw);

    if (result.success) {
      return result.data as z.infer<T>;
    }

    // Graceful degradation: use defaults from schema
    const defaults: any = {};
    Object.entries(schema.shape).forEach(([key, zodType]: [string, any]) => {
      if (zodType instanceof z.ZodDefault) {
        defaults[key] = (zodType._def.defaultValue as () => any)();
      } else if (zodType instanceof z.ZodCatch) {
        defaults[key] = zodType._def.catchValue;
      }
    });

    return defaults as z.infer<T>;
  }, [searchParams, schema]);

  // Type-safe setter
  const setParams = useCallback(
    (updates: Partial<z.infer<T>>) => {
      setSearchParams((prev) => {
        const current = Object.fromEntries(prev.entries());
        const merged = { ...current, ...updates };

        // Remove undefined values
        Object.keys(merged).forEach((key) => {
          if (merged[key] === undefined) delete merged[key];
        });

        return merged;
      });
    },
    [setSearchParams],
  );

  return [params, setParams] as const;
}
